import { Location, Editor, Range, Path, Point } from 'domains/reporter/RichTextEditor/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Macro } from 'generated/graphql';
import { Transforms, Element } from 'slate';
import clone from 'ramda/es/clone';
import prop from 'ramda/es/prop';
import uniqBy from 'ramda/es/uniqBy';
import { getPicklistsNodesFromParentNode } from '../../../RichTextEditor/plugins/picklist/utils';
import { nanoid } from 'nanoid';
import { PICKLIST_PLUGIN_ID } from '../../../RichTextEditor/plugins/picklist';
import { partialEditor, walkSlateContent } from '../../../RichTextEditor/utils';
import { unfurlMacroPlaceholderStatic } from '../../../RichTextEditor/plugins/macroPlaceholder';
import { usePicklistStore } from '../../../Template/usePicklist';
import type { EnhanceEditorState } from '../../../RichTextEditor/types';
import { stitchNodesIntoEditor } from '../../../RichTextEditor/stitching';
import {
  isOnlyHeadingSelected,
  isPointAfterSelectionInsideHeading,
} from '../../../RichTextEditor/plugins/heading/utils/normalization';
import { useToasterDispatch } from 'common/ui/Toaster/Toaster';
import type { ToastKey } from 'common/ui/Toaster/Toast';
import { logger } from 'modules/logger';
import { useCurrentWorklistItems } from 'hooks/useCurrentWorklistItems';
import { hydratePlaceholders } from '../../../RichTextEditor/plugins/placeholder/utils';
import {
  fragmentContainsBrackets,
  fragmentContainsNodeType,
} from '../../../RichTextEditor/stitching/fragmentHelpers';
import { prepFragmentAndInsertionPoint } from '../../../RichTextEditor/plugins/inlineBookmark/utils';
import { atom, useSetRecoilState } from 'recoil';
import type { RecoilState } from 'recoil';
import type { Macros } from '../../../Template/usePicklist';
import { PicklistPluginElement } from 'domains/reporter/RichTextEditor/slate-custom-types';
import { PARAGRAPH_PLUGIN_ID } from 'domains/reporter/RichTextEditor/plugins/paragraph/types';
import { slateSource, type SlateSource } from 'domains/reporter/RichTextEditor/core/slateSource';
import { annotateTextWithSource } from 'domains/reporter/RichTextEditor/plugins/textSourceStyling/utils';
import { isInsideListAtPath } from 'domains/reporter/RichTextEditor/plugins/list/utils';
import { LIST_PLUGIN_ID } from 'domains/reporter/RichTextEditor/plugins/list/types';
import { BRACKET_TYPES } from 'domains/reporter/RichTextEditor/constants';
import { convertParagraphsIntoLineBreaks } from 'domains/reporter/RichTextEditor/plugins/lineBreak/utils';

export type UseMacrosReturn = (arg1: {
  editor?: Editor;
  editorStateEnhancers?: EnhanceEditorState[];
  macro: Macro;
  at?: Location;
  source?: SlateSource;
}) => void;

export const isInsertingMacroState: RecoilState<boolean> = atom({
  key: 'isInsertingMacro',
  default: false,
});

export const useInsertMacro = (): UseMacrosReturn => {
  const { setPicklists, setMacros } = usePicklistStore();
  const { enqueueOrUpdateToast, enqueueToast } = useToasterDispatch();
  const { currentWorklistItems } = useCurrentWorklistItems();
  const [latestToastKey, setLatestToastKey] = useState<Nullable<ToastKey>>(null);
  const setIsInsertingMacro = useSetRecoilState(isInsertingMacroState);
  const currentWorklistItemsRef = useRef(currentWorklistItems);

  useEffect(() => {
    // Keep `insertMacro` stable (RightDrawer was rerendering whenever currentWorklistItems > insertMacro changed),
    // but still expose the freshest worklist data inside the callback via this ref.
    currentWorklistItemsRef.current = currentWorklistItems;
  }, [currentWorklistItems]);

  const insertMacro = useCallback(
    ({
      editor,
      editorStateEnhancers,
      macro,
      at,
      source,
    }: {
      editor?: Editor;
      editorStateEnhancers?: EnhanceEditorState[];
      macro: Macro;
      at?: Location;
      source?: SlateSource;
    }) => {
      if (!editor || !editorStateEnhancers) {
        const editorErrorMessage =
          '[useInsertMacro] Cannot insert macro because there is no active editor!';
        logger.warn(editorErrorMessage, {
          logID: JSON.stringify(at),
          macro,
          editor: editor != null ? partialEditor(editor) : 'null',
        });
        return;
      }

      let selection = editor.selection;

      if (selection == null) {
        const selectionErrorMessage =
          '[useInsertMacro] Cannot insert macro because there is no selection!';
        logger.warn(selectionErrorMessage, {
          logID: JSON.stringify(at),
          macro,
          editor: partialEditor(editor),
        });
        return;
      }

      // Cannot insert macro into heading
      if (isOnlyHeadingSelected(editor)) {
        // check if point after current selection is in header. if so, trigger toast.
        // else, move the selection to point after and continue with macro insertion
        if (isPointAfterSelectionInsideHeading(editor)) {
          const toastKey = enqueueOrUpdateToast(
            'You cannot insert macros into a heading.',
            latestToastKey,
            {
              severity: 'error',
            }
          );
          setLatestToastKey(toastKey);
          return;
        } else {
          Transforms.move(editor, { distance: 1 });
          selection = editor.selection;

          if (selection == null) {
            const selectionErrorMessage =
              '[useInsertMacro] Cannot insert macro because there is no selection!';
            logger.warn(selectionErrorMessage, {
              logID: JSON.stringify(at),
              macro,
              editor: partialEditor(editor),
            });
            return;
          }
        }
      }

      setIsInsertingMacro(true);

      const newMacroBase = clone(macro);
      const macroContainsFields =
        macro.picklists.length > 0 ||
        macro.nestedMacros.length > 0 ||
        fragmentContainsBrackets(Array.from(macro.text));

      const prepLocation = Range.isRange(at) ? at : selection;
      let [fragment, location] = prepFragmentAndInsertionPoint(
        editor,
        [...newMacroBase.text],
        macroContainsFields,
        {
          location: prepLocation,
          removeEmptyBrackets: true,
        }
      );

      if (location == null) {
        location = selection;
      }

      const macroText = fragment as Macro['text'];

      // Searches for picklistsIDs on the Editor and on the new macro.
      // If a picklist exists on the editor and the new macro tries to insert the same id
      // Then the new macro picklists will have a new generated picklistID with nanoid()
      // This logic prevents the generation of picklist elements with the same ID
      const editorPicklistsIds = getPicklistsNodesFromParentNode(editor).map(
        ({ picklistID }) => picklistID
      );

      // Stores the id of the old picklist as a key and the new id as the value
      const newPicklistMap = new Map<string, string>();
      macro.picklists
        .filter(({ id }) => editorPicklistsIds.includes(id))
        .forEach(({ id }) => {
          newPicklistMap.set(id, nanoid());
        });

      const onElement = (node: Element) => {
        const newPicklistID = newPicklistMap.get((node as PicklistPluginElement).picklistID);
        if (node.type === PICKLIST_PLUGIN_ID && newPicklistID != null) {
          node.picklistID = newPicklistID;
        }
      };
      walkSlateContent(onElement)(fragment);

      const updatedPicklists = macro.picklists.map((picklist) => {
        const newPicklistID = newPicklistMap.get(picklist.id);
        return {
          ...picklist,
          ...(newPicklistID != null
            ? {
                id: newPicklistID,
              }
            : {}),
        };
      });

      const newMacro: Macro = {
        ...newMacroBase,
        text: macroText,
        picklists: updatedPicklists,
      };

      const nestedMacros = newMacro.nestedMacros ?? [];
      const unfurledContent = unfurlMacroPlaceholderStatic(
        fragment,
        nestedMacros,
        editorStateEnhancers,
        {
          fragmentMode: true,
        }
      );

      // If the first node in the macro is a paragraph, pass that paragraph's children as fragments along with subsequent nodes.
      // This ensures that macros are rendered in-line at left rather than on a new line, while respecting other structure.
      let newNodes = unfurledContent;
      if (
        Element.isElement(unfurledContent[0]) &&
        unfurledContent[0].type === PARAGRAPH_PLUGIN_ID
      ) {
        newNodes = [...unfurledContent[0].children, ...unfurledContent.slice(1)];
      }
      const sourceToApply = source ?? slateSource.ugc.macro;
      const shouldOverwriteExistingSource = sourceToApply === slateSource.ai.focusMode;

      const insertionLocation = at ?? location ?? selection;
      if (
        insertionLocation == null ||
        (!Range.isRange(insertionLocation) &&
          !Path.isPath(insertionLocation) &&
          !Point.isPoint(insertionLocation))
      ) {
        setIsInsertingMacro(false);
        return;
      }

      const insertionPoint = Range.isRange(insertionLocation)
        ? insertionLocation.anchor
        : Point.isPoint(insertionLocation)
          ? insertionLocation
          : Editor.start(editor, insertionLocation);

      // When inserting a multiline macro inside a bracket field within a list item,
      // convert paragraph blocks to line breaks so the content stays in the same list item.
      // Skip conversion when the macro contains block-level elements (e.g. lists) —
      // the stitching logic handles splitting the bracket for block insertion.
      const macroContainsBlockElements = fragmentContainsNodeType(newNodes, LIST_PLUGIN_ID);
      const shouldConvertMacroParagraphsToLineBreaks =
        !macroContainsBlockElements &&
        isInsideListAtPath(editor, insertionPoint.path) &&
        Editor.above(editor, {
          at: insertionPoint,
          match: (node) => Element.isElement(node) && BRACKET_TYPES.includes(node.type),
          mode: 'lowest',
        }) != null;

      if (shouldConvertMacroParagraphsToLineBreaks) {
        newNodes = convertParagraphsIntoLineBreaks(newNodes);
      }

      newNodes = annotateTextWithSource(newNodes, {
        source: sourceToApply,
        overwriteSource: shouldOverwriteExistingSource,
      });

      stitchNodesIntoEditor(editor, newNodes, {
        at: insertionLocation,
        select: true,
        enableLogging: true,
        enqueueToast,
        isInsertingMacro: true,
      });

      setPicklists((statePicklists) => [...statePicklists, ...newMacro.picklists]);
      setMacros((stateMacros: Macros): Macros => {
        const currentMacros = stateMacros ?? [];
        const macros = [...currentMacros, newMacro, ...newMacro.nestedMacros];
        const newMacros = uniqBy(prop('smid'), macros);
        return newMacros;
      });

      hydratePlaceholders(editor, currentWorklistItemsRef.current);

      setIsInsertingMacro(false);
    },
    [
      setIsInsertingMacro,
      enqueueToast,
      setPicklists,
      setMacros,
      enqueueOrUpdateToast,
      latestToastKey,
    ]
  );

  return insertMacro;
};
